import React, { useState, useEffect } from 'react';
import GeneratorPanel from './GeneratorPanel';
import IntroPanel from './IntroPanel';
import generate from './MandGen';
import coordId from './coordId';
import { toCanvasPoint } from './pointer';

// Render resolution is capped for performance (per-pixel escape velocity runs in
// JS); the image is then CSS-scaled to fill the viewport. The render is sized to
// the viewport's OWN aspect ratio so filling 100%x100% neither crops nor
// distorts the fractal — and portrait fills the screen instead of leaving a void.
const RENDER_CAP = 1000;

// Generous bounds of the whole Mandelbrot set, used to frame the opening view.
const SET_CENTER = { r: -0.5, i: 0.0 };
const SET_W = 3.0;
const SET_H = 2.7;

const computeCanvasDimensions = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const aspect = vw / vh;
    let width, height;
    if (aspect >= 1) {
        width = Math.min(RENDER_CAP, Math.round(vw));
        height = Math.round(width / aspect);
    } else {
        height = Math.min(RENDER_CAP, Math.round(vh));
        width = Math.round(height * aspect);
    }
    return { width: Math.max(1, width), height: Math.max(1, height) };
};

// Frame the entire set inside the viewport aspect without distortion: fit
// whichever dimension is the binding constraint, centre on the set.
const fitWholeSet = (aspect) => {
    let realSpan, imagSpan;
    if (aspect > SET_W / SET_H) {
        imagSpan = SET_H;
        realSpan = imagSpan * aspect;
    } else {
        realSpan = SET_W;
        imagSpan = realSpan / aspect;
    }
    return {
        realMin: SET_CENTER.r - realSpan / 2,
        realMax: SET_CENTER.r + realSpan / 2,
        imaginaryMin: SET_CENTER.i - imagSpan / 2,
        imaginaryMax: SET_CENTER.i + imagSpan / 2
    };
};

const Display = () => {

    const [canvasDims, setCanvasDims] = useState(computeCanvasDimensions);

    const cw = canvasDims.width;
    const ch = canvasDims.height;

    const [zooms, setZooms] = useState([]);
    const [display, setDisplay] = useState(null);

    // Stored as single-element arrays and mutated in place by the drag handlers
    // (a deliberate quirk of this code); the state setters are intentionally unused.
    const [topLeftX] = useState([0]);
    const [topLeftY] = useState([0]);
    const [bottomRightX] = useState([cw]);
    const [bottomRightY] = useState([ch]);

    const startBounds = fitWholeSet(window.innerWidth / window.innerHeight);
    const [minr, setMinr] = useState([startBounds.realMin]);
    const [maxr, setMaxr] = useState([startBounds.realMax]);
    const [mini, setMini] = useState([startBounds.imaginaryMin]);
    const [maxi, setMaxi] = useState([startBounds.imaginaryMax]);
    const [dragMode, setDragMode] = useState(false);
    const [graphicsCtx, setGraphicsCtx] = useState(null);

    useEffect(() => {
        const handleResize = () => {
            const dims = computeCanvasDimensions();
            setCanvasDims(dims);
            // Before the first render, keep the opening view fitted to the
            // viewport so Begin frames the whole set correctly. After the user
            // has started zooming, their region is preserved (it re-fits to the
            // canvas aspect on the next zoom).
            if (zooms.length === 0) {
                const b = fitWholeSet(dims.width / dims.height);
                setMinr([b.realMin]);
                setMaxr([b.realMax]);
                setMini([b.imaginaryMin]);
                setMaxi([b.imaginaryMax]);
            }
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [zooms.length]);

    const zoom = () => {
        let zoomResponse = generate({
            manual: false,
            width: cw,
            height: ch,
            mousePositions: {
                tlx: topLeftX[0],
                tly: topLeftY[0],
                brx: bottomRightX[0],
                bry: bottomRightY[0]
            },
            axisParams: {
                realMin: minr[0],
                realmax: maxr[0],
                imaginaryMin: mini[0],
                imaginaryMax: maxi[0]
            }
        });
        applyZoom(zoomResponse);
    };

    // First render from the hero: frame the whole current (fitted) region.
    const beginRender = () => {
        topLeftX[0] = 0;
        topLeftY[0] = 0;
        bottomRightX[0] = cw;
        bottomRightY[0] = ch;
        zoom();
    };

    const applyZoom = (zoomResponse) => {
        setDisplay(zoomResponse.dataUrl);
        setZooms(prev => [...prev, zoomResponse]);
        setMinr([zoomResponse.realMin]);
        setMaxr([zoomResponse.realMax]);
        setMini([zoomResponse.imaginaryMin]);
        setMaxi([zoomResponse.imaginaryMax]);
    };

    const reloadZoom = (oldZoom) => {
        setDisplay(oldZoom.dataUrl);
        setMinr([oldZoom.realMin]);
        setMaxr([oldZoom.realMax]);
        setMini([oldZoom.imaginaryMin]);
        setMaxi([oldZoom.imaginaryMax]);
    };

    const manualZoom = () => {
        const manualMinR = document.getElementById('real-min-in').valueAsNumber;
        const manualMaxR = document.getElementById('real-max-in').valueAsNumber;
        const manualMinI = document.getElementById('imaginary-min-in').valueAsNumber;
        const manualMaxI = document.getElementById('imaginary-max-in').valueAsNumber;

        if ([manualMinR, manualMaxR, manualMinI, manualMaxI].some(v => Number.isNaN(v))) {
            return;
        }

        const response = generate({
            manual: true,
            width: cw,
            height: ch,
            axisValues: {
                newMinR: manualMinR,
                newMaxR: manualMaxR,
                newMinI: manualMinI,
                newMaxI: manualMaxI
            }
        });
        applyZoom(response);
    };

    // Always measure the tracker canvas (a single fixed element), never the
    // event target — a pointer can land on either the <img> or the overlapping
    // canvas, and those must not give different reference rects. Convert the
    // screen point into canvas pixels here so everything downstream works in one
    // space, regardless of display size.
    const toCanvas = (clientX, clientY) => {
        const rect = document.getElementById('tracker').getBoundingClientRect();
        return toCanvasPoint(clientX, clientY, rect, cw, ch);
    };

    // Lock the selection to the canvas (=viewport) aspect ratio around the start
    // corner, so the boxed region always renders without distortion — the
    // viewfinder shows exactly the framing you'll get.
    const lockedCorner = (x0, y0, x, y) => {
        const aspect = cw / ch;
        const dx = x - x0;
        const dy = y - y0;
        let w = Math.abs(dx);
        let h = Math.abs(dy);
        if (h === 0 || w / h > aspect) {
            h = w / aspect;
        } else {
            w = h * aspect;
        }
        const cx = Math.min(Math.max(x0 + (dx < 0 ? -w : w), 0), cw);
        const cy = Math.min(Math.max(y0 + (dy < 0 ? -h : h), 0), ch);
        return { x: cx, y: cy };
    };

    const drawViewfinder = (x0, y0, x1, y1) => {
        const ctx = graphicsCtx;
        ctx.clearRect(0, 0, cw, ch);

        const left = Math.min(x0, x1);
        const right = Math.max(x0, x1);
        const top = Math.min(y0, y1);
        const bottom = Math.max(y0, y1);

        ctx.strokeStyle = '#ECE7DC';
        ctx.lineWidth = Math.max(1, cw / 800);

        // faint framing rectangle
        ctx.globalAlpha = 0.5;
        ctx.strokeRect(left, top, right - left, bottom - top);

        // crisp corner ticks
        ctx.globalAlpha = 1;
        const t = Math.min(right - left, bottom - top) * 0.18;
        ctx.beginPath();
        ctx.moveTo(left, top + t); ctx.lineTo(left, top); ctx.lineTo(left + t, top);
        ctx.moveTo(right - t, top); ctx.lineTo(right, top); ctx.lineTo(right, top + t);
        ctx.moveTo(right, bottom - t); ctx.lineTo(right, bottom); ctx.lineTo(right - t, bottom);
        ctx.moveTo(left + t, bottom); ctx.lineTo(left, bottom); ctx.lineTo(left, bottom - t);
        ctx.stroke();
    };

    const beginDrag = (clientX, clientY) => {
        const { x, y } = toCanvas(clientX, clientY);
        topLeftX[0] = x;
        topLeftY[0] = y;

        const ctx = document.getElementById('tracker').getContext('2d');
        setGraphicsCtx(ctx);
        setDragMode(true);
    };

    const moveDrag = (clientX, clientY) => {
        if (!dragMode || !graphicsCtx) return;
        const { x, y } = toCanvas(clientX, clientY);
        const c = lockedCorner(topLeftX[0], topLeftY[0], x, y);
        bottomRightX[0] = c.x;
        bottomRightY[0] = c.y;
        drawViewfinder(topLeftX[0], topLeftY[0], c.x, c.y);
    };

    const endDrag = (clientX, clientY) => {
        if (!dragMode) return;
        const { x, y } = toCanvas(clientX, clientY);
        const c = lockedCorner(topLeftX[0], topLeftY[0], x, y);
        bottomRightX[0] = c.x;
        bottomRightY[0] = c.y;

        setDragMode(false);
        if (graphicsCtx) graphicsCtx.clearRect(0, 0, cw, ch);

        // Ignore a tap / zero-area drag (easy on touch) — it would zoom to a
        // degenerate region and paint nothing.
        if (Math.abs(c.x - topLeftX[0]) < 3 || Math.abs(c.y - topLeftY[0]) < 3) return;

        zoom();
    };

    const handleMouseDown = (event) => {
        event.preventDefault();
        beginDrag(event.clientX, event.clientY);
    };

    const handleMouseMove = (event) => {
        moveDrag(event.clientX, event.clientY);
    };

    const handleMouseRelease = (event) => {
        event.preventDefault();
        endDrag(event.clientX, event.clientY);
    };

    const handleTouchStart = (event) => {
        event.preventDefault();
        beginDrag(event.touches[0].clientX, event.touches[0].clientY);
    };

    const handleTouchMove = (event) => {
        event.preventDefault();
        moveDrag(event.touches[0].clientX, event.touches[0].clientY);
    };

    const handleTouchEnd = (event) => {
        event.preventDefault();
        endDrag(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
    };

    const current = {
        realMin: minr[0],
        realMax: maxr[0],
        imaginaryMin: mini[0],
        imaginaryMax: maxi[0]
    };
    current.id = coordId(current);

    return (
        <div id="main-display">
            {zooms.length > 0
                ? <GeneratorPanel
                        zooms={zooms}
                        display={display}
                        current={current}
                        cw={cw}
                        ch={ch}
                        handleMouseDown={handleMouseDown}
                        handleMouseRelease={handleMouseRelease}
                        handleMouseMove={handleMouseMove}
                        handleTouchStart={handleTouchStart}
                        handleTouchMove={handleTouchMove}
                        handleTouchEnd={handleTouchEnd}
                        reloadZoom={reloadZoom}
                        manualZoom={manualZoom} />
                : <IntroPanel onBegin={beginRender} />}
        </div>
    );
};

export default Display;
